/**
 * Lead-recovery checkout offers: first charge can be lower than the recurring
 * amount, and (case 1) Premium at Basic price auto-downgrades after a month.
 *
 * Public /plans prices never change. These only apply when a valid offer token
 * is present.
 */
import type { BillingInterval, Currency, Plan } from "./types.js";
import { getPlanPricing } from "./plans.js";

export const CHECKOUT_OFFER_KINDS = [
  "premium_at_basic",
  "first_month_2499",
  "first_month_1499",
] as const;

export type CheckoutOfferKind = (typeof CHECKOUT_OFFER_KINDS)[number];

export function isCheckoutOfferKind(value: string): value is CheckoutOfferKind {
  return (CHECKOUT_OFFER_KINDS as readonly string[]).includes(value);
}

/** First-month promotional amounts for the two "pay less then list" offers. */
const FIRST_MONTH_FIXED_CENTS: Record<"first_month_2499" | "first_month_1499", Record<Currency, number>> = {
  first_month_2499: { BRL: 2499, USD: 499 },
  first_month_1499: { BRL: 1499, USD: 299 },
};

export interface CheckoutOfferPricing {
  kind: CheckoutOfferKind;
  plan: Plan;
  interval: BillingInterval;
  currency: Currency;
  /** Charged today. */
  firstCents: number;
  /** What the provider should charge from the 2nd cycle if the customer does nothing. */
  recurringCents: number;
  /** Catalog list of the chosen plan (display). */
  listCents: number;
  /**
   * PIX bank-app ceiling for the plan alone: Premium list. The checkout asks for this plus the paid
   * add-ons (`offerAmountsWithAddOns`).
   */
  authorizedCeilingCents: number;
  /** Case 1 locks the picker to Premium. */
  lockedPlan: Plan | null;
  /** Case 1: schedule Premium → Basic at period end, keeping recurringCents. */
  scheduleDowngradeToBasic: boolean;
  /** Stripe: create at recurringCents and discount the first invoice. */
  firstInvoiceDiscount: boolean;
  /** OpenPIX: send minimumValue (variable journey). Only on this path. */
  openpixVariable: boolean;
}

export function computeCheckoutOfferPricing(args: {
  kind: CheckoutOfferKind;
  plan: Plan;
  currency: Currency;
}): CheckoutOfferPricing {
  const interval: BillingInterval = "monthly";
  const listCents = getPlanPricing(args.plan, interval, args.currency).totalCents;
  const basicListCents = getPlanPricing("basic", interval, args.currency).totalCents;
  const premiumListCents = getPlanPricing("premium", interval, args.currency).totalCents;

  if (args.kind === "premium_at_basic") {
    return {
      kind: args.kind,
      plan: "premium",
      interval,
      currency: args.currency,
      firstCents: basicListCents,
      recurringCents: basicListCents,
      listCents: premiumListCents,
      authorizedCeilingCents: offerAuthorizedCeilingCents(args.currency),
      lockedPlan: "premium",
      scheduleDowngradeToBasic: true,
      firstInvoiceDiscount: false,
      openpixVariable: true,
    };
  }

  const firstCents = FIRST_MONTH_FIXED_CENTS[args.kind][args.currency];
  return {
    kind: args.kind,
    plan: args.plan,
    interval,
    currency: args.currency,
    firstCents,
    recurringCents: listCents,
    listCents,
    authorizedCeilingCents: offerAuthorizedCeilingCents(args.currency),
    lockedPlan: null,
    scheduleDowngradeToBasic: false,
    firstInvoiceDiscount: firstCents < listCents,
    openpixVariable: true,
  };
}

/**
 * The PIX ceiling an offer asks the payer to authorize: Premium list plus the paid add-ons
 * (monthly, as offers are), the most month 2 or a 1-click Premium can charge.
 */
export function offerAuthorizedCeilingCents(currency: Currency, addOnMonthlyCents = 0): number {
  return getPlanPricing("premium", "monthly", currency).totalCents + addOnMonthlyCents;
}

/**
 * An offer's recurring amount and PIX ceiling with the paid add-ons the buyer ticked (monthly
 * cents: offers are monthly). Add-ons are never discounted: the first charge adds them as on any
 * checkout, and so do these two, so the Stripe first-invoice discount stays the same and month 2
 * and a 1-click Premium still fit under what the payer authorized.
 */
export function offerAmountsWithAddOns(
  pricing: Pick<CheckoutOfferPricing, "recurringCents" | "authorizedCeilingCents">,
  addOnMonthlyCents: number,
): { recurringCents: number; authorizedCeilingCents: number } {
  return {
    recurringCents: pricing.recurringCents + addOnMonthlyCents,
    authorizedCeilingCents: pricing.authorizedCeilingCents + addOnMonthlyCents,
  };
}

export function offerRejectsPlan(kind: CheckoutOfferKind, plan: Plan): boolean {
  return kind === "premium_at_basic" && plan !== "premium";
}

export function offerRejectsInterval(interval: BillingInterval): boolean {
  return interval !== "monthly";
}

/** Case 1 goes straight to Premium checkout. First-month offers land on /plans. */
export function buildCheckoutOfferUrl(args: {
  publicAppUrl: string;
  token: string;
  kind: CheckoutOfferKind;
  locale: string;
  salesToken?: string | null;
  email?: string | null;
  phone?: string | null;
}): string {
  const params = new URLSearchParams({
    offer: args.token,
    app_language: args.locale,
  });
  if (args.salesToken) params.set("sales_token", args.salesToken);
  if (args.email) params.set("email", args.email);
  if (args.phone) params.set("phone", args.phone);

  let path = "/plans";
  if (args.kind === "premium_at_basic") {
    path = "/checkout";
    params.set("plan", "premium");
    params.set("interval", "monthly");
  } else {
    params.set("interval", "monthly");
  }
  return `${args.publicAppUrl.replace(/\/$/, "")}${path}?${params.toString()}`;
}

/**
 * True when we can raise the recurring amount to Premium without a new
 * Checkout / PIX QR — the original offer already asked the customer to
 * authorize a ceiling that covers Premium and the add-ons the raise keeps.
 */
export function canRaiseInPlaceToPremium(args: {
  authorizedCeilingCents: number | null | undefined;
  premiumListCents: number;
  /** Paid add-ons the subscription keeps, monthly: the raise charges Premium plus these. */
  addOnMonthlyCents?: number;
  providerSubscriptionId: string | null | undefined;
  targetPlan: Plan;
  targetInterval: BillingInterval;
  currentPaymentMethod: string;
  targetPaymentMethod: string;
  status?: string | null;
  openpixVariable?: boolean | null;
  /**
   * False once the offer's first paid period has ended (a renewal moved
   * `currentPeriodStart`, or the offer was never redeemed). Omitted means the
   * caller has not checked yet.
   */
  offerPeriodActive?: boolean;
}): boolean {
  if (args.offerPeriodActive === false) return false;
  if (args.targetPlan !== "premium") return false;
  if (args.targetInterval !== "monthly") return false;
  if (args.currentPaymentMethod !== args.targetPaymentMethod) return false;
  if (!args.providerSubscriptionId?.trim()) return false;
  if (args.authorizedCeilingCents == null) return false;
  if (args.authorizedCeilingCents < args.premiumListCents + (args.addOnMonthlyCents ?? 0)) return false;
  if (args.status && args.status !== "active" && args.status !== "scheduled_change") return false;
  if (args.currentPaymentMethod === "openpix" && args.openpixVariable !== true) return false;
  return true;
}

export const OFFER_DEFAULT_EXPIRES_IN_DAYS = 30;

export function offerExpiresAt(now = new Date(), expiresInDays = OFFER_DEFAULT_EXPIRES_IN_DAYS): Date {
  return new Date(now.getTime() + expiresInDays * 24 * 60 * 60 * 1000);
}

/** First paid period: period start still lines up with offer redemption. */
export const OFFER_FIRST_PERIOD_TOLERANCE_MS = 48 * 60 * 60 * 1000;

export function isOfferFirstPeriod(args: {
  offerRedeemedAt: Date | null | undefined;
  currentPeriodStart: Date | null | undefined;
}): boolean {
  if (!args.offerRedeemedAt || !args.currentPeriodStart) return false;
  return (
    Math.abs(args.currentPeriodStart.getTime() - args.offerRedeemedAt.getTime()) <=
    OFFER_FIRST_PERIOD_TOLERANCE_MS
  );
}

export function isPremiumAtBasicOfferDowngrade(row: {
  kind: string;
  payload?: unknown;
}): boolean {
  if (row.kind !== "downgrade_plan") return false;
  const payload = row.payload as { source?: string } | null;
  return payload?.source === "premium_at_basic_offer";
}
